import type {
  AuthUser,
  ManageableOrganizationsSummary,
  Membership,
  UserSettings,
  ViewAsSessionInfo,
} from "../types";
import type {
  AuthRequestOptions,
  OrganizationAccessStatus,
  RefreshManageableOrganizationsOptions,
} from "./authContextSupport";

export interface AuthContextValue {
  authError: string;
  authErrorTitle: string;
  canManageOrganizations: boolean;
  fetchUserSettings: (options?: AuthRequestOptions) => Promise<void>;
  handleLoginSuccess: () => Promise<void>;
  handleLogout: () => Promise<void>;
  hasOrgAccess: (orgName: string) => boolean;
  hasOrganizationContext: boolean;
  hasPermission: (permission: string) => boolean | undefined;
  isAdmin: boolean;
  /** Internal team label; shapes which staff-only tools are shown. Never a
   * permission — see utils/staffTeam.ts. */
  staffTeam: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  markOrganizationAccessDenied: () => void;
  manageableOrganizationsCount: number;
  memberships: Membership[];
  organizationAccessStatus: OrganizationAccessStatus;
  refreshManageableOrganizations: (
    options?: RefreshManageableOrganizationsOptions,
  ) => Promise<ManageableOrganizationsSummary>;
  user: AuthUser | null;
  userSettings: UserSettings | null;
  viewAs: ViewAsSessionInfo | null;
}

export function userHasPermission(
  user: AuthUser | null,
  canManageOrganizations: boolean,
  permission: string,
): boolean | undefined {
  if (!user) return false;
  if (user.isAdmin) return true;
  switch (permission) {
    case "view_all_orgs":
    case "manage_orgs":
    case "view_members":
    case "manage_members":
    case "view_analytics":
      return canManageOrganizations;
    case "view_chatbots":
    case "view_conversations":
      return true;
    case "edit_chatbots":
    case "admin_panel":
    case "experiments:manage":
      return user.isAdmin;
    default:
      return false;
  }
}
